import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { useAuthToken } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const AuthShell = ({ title, subtitle, children }) => (
  <div className="min-h-screen grid lg:grid-cols-2 bg-[#F9F8F6]">
    <div className="hidden lg:flex flex-col justify-between p-10 bg-[#1E2A38] text-white relative overflow-hidden">
      <div className="relative z-10">
        <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-3 py-1 text-xs font-mono">reseller.goweb.srl</div>
        <h1 className="mt-10 text-4xl font-bold tracking-tight leading-tight">Il portale dei<br />Rivenditori <span className="text-[#C69214]">GoWeb</span>.</h1>
        <p className="mt-4 text-slate-300 max-w-sm">Gestisca clienti, prodotti, provvigioni e documentazione in un'unica piattaforma.</p>
      </div>
      <div className="relative z-10 text-xs text-slate-400">© GoWeb Srl Semplificata — Booking Hub · Cantiere 360 Cloud</div>
      <div className="absolute right-[-80px] top-[-80px] w-[380px] h-[380px] rounded-full bg-[#B84A2B]/35 blur-3xl" />
      <div className="absolute left-[-60px] bottom-[-60px] w-[320px] h-[320px] rounded-full bg-[#C69214]/25 blur-3xl" />
    </div>
    <div className="flex items-center justify-center p-6">
      <div className="w-full max-w-sm bg-white border border-[#E2DDD5] rounded-2xl p-7 shadow-sm" data-testid="auth-card">
        <h2 className="text-xl font-bold tracking-tight">{title}</h2>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
        <div className="mt-6">{children}</div>
      </div>
    </div>
  </div>
);

const errMsg = (e) => e.response?.data?.detail || "Errore di connessione";

function useAuthForm(onSubmit) {
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await onSubmit(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return { busy, submit };
}

const Field = ({ label, testid, ...props }) => (
  <div><Label className="text-xs">{label}</Label><Input {...props} data-testid={testid} className="mt-1" /></div>
);

export function SignInPage() {
  const nav = useNavigate();
  const { setToken } = useAuthToken();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { busy, submit } = useAuthForm(async () => {
    const r = await axios.post(`${API}/auth/login`, { email, password });
    setToken(r.data.token);
    nav("/", { replace: true });
  });
  return (
    <AuthShell title="Accedi al Portale Rivenditori GoWeb" subtitle="Inserisca le Sue credenziali per continuare">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} testid="signin-email" />
        <Field label="Password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} testid="signin-password" />
        <Button type="submit" disabled={busy} className="w-full bg-[#B84A2B] hover:bg-[#983C21] text-white" data-testid="signin-submit">Accedi</Button>
      </form>
      <div className="mt-4 flex justify-between text-sm">
        <Link to="/forgot-password" className="text-[#B84A2B] hover:underline" data-testid="link-forgot">Password dimenticata?</Link>
        <Link to="/sign-up" className="text-slate-600 hover:text-[#B84A2B]" data-testid="link-signup">Registrati</Link>
      </div>
    </AuthShell>
  );
}

export function SignUpPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { token, setToken, logout } = useAuthToken();
  const [f, setF] = useState({ email: params.get("email") || "", password: "", confirm: "" });
  const { busy, submit } = useAuthForm(async () => {
    if (f.password !== f.confirm) throw { response: { data: { detail: "Le password non coincidono" } } };
    const r = await axios.post(`${API}/auth/register`, { email: f.email, password: f.password });
    setToken(r.data.token);
    toast.success("Account creato");
    nav("/", { replace: true });
  });
  if (token) {
    return (
      <AuthShell title="È già connesso al portale" subtitle="Per registrare un nuovo account rivenditore deve prima uscire dall'account attuale.">
        <div className="space-y-3" data-testid="signup-logged-in">
          <Button onClick={logout} className="w-full bg-[#B84A2B] hover:bg-[#983C21] text-white" data-testid="signup-logout-continue">Esci e registrati</Button>
          <Button variant="outline" onClick={() => nav("/", { replace: true })} className="w-full" data-testid="signup-back">Torna al mio account</Button>
        </div>
      </AuthShell>
    );
  }
  return (
    <AuthShell title="Diventa rivenditore GoWeb" subtitle="Crei il Suo account: poi completerà anagrafica e documenti.">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} testid="signup-email" />
        <Field label="Password (almeno 6 caratteri)" type="password" required minLength={6} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} testid="signup-password" />
        <Field label="Ripeti password" type="password" required minLength={6} value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} testid="signup-confirm" />
        <Button type="submit" disabled={busy} className="w-full bg-[#B84A2B] hover:bg-[#983C21] text-white" data-testid="signup-submit">Crea account</Button>
      </form>
      <p className="mt-4 text-sm text-slate-600">Ha già un account? <Link to="/sign-in" className="text-[#B84A2B] hover:underline" data-testid="link-signin">Accedi</Link></p>
    </AuthShell>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const { busy, submit } = useAuthForm(async () => {
    await axios.post(`${API}/auth/forgot-password`, { email, reset_url: `${window.location.origin}/reset-password` });
    setSent(true);
  });
  return (
    <AuthShell title="Password dimenticata" subtitle="Le invieremo un link per sceglierne una nuova.">
      {sent ? (
        <p className="text-sm text-slate-700" data-testid="forgot-sent">Se l'email è registrata, riceverà a breve un link valido 1 ora. Controlli anche lo spam.</p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Field label="Email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} testid="forgot-email" />
          <Button type="submit" disabled={busy} className="w-full bg-[#B84A2B] hover:bg-[#983C21] text-white" data-testid="forgot-submit">Invia link</Button>
        </form>
      )}
      <p className="mt-4 text-sm"><Link to="/sign-in" className="text-[#B84A2B] hover:underline">Torna all'accesso</Link></p>
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { setToken } = useAuthToken();
  const [f, setF] = useState({ password: "", confirm: "" });
  const { busy, submit } = useAuthForm(async () => {
    if (f.password !== f.confirm) throw { response: { data: { detail: "Le password non coincidono" } } };
    const r = await axios.post(`${API}/auth/reset-password`, { token: params.get("token") || "", password: f.password });
    setToken(r.data.token);
    toast.success("Password aggiornata");
    nav("/", { replace: true });
  });
  return (
    <AuthShell title="Scelga una nuova password" subtitle="Almeno 6 caratteri.">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Nuova password" type="password" required minLength={6} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} testid="reset-password" />
        <Field label="Ripeti password" type="password" required minLength={6} value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} testid="reset-confirm" />
        <Button type="submit" disabled={busy} className="w-full bg-[#B84A2B] hover:bg-[#983C21] text-white" data-testid="reset-submit">Salva password</Button>
      </form>
    </AuthShell>
  );
}
